import React, { useState, useEffect } from 'react';
import { useAuth, useCart } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, Package, Palette, Lock, Check, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopPage = () => {
  const { user } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({}); // { productId: { color: 'Black', pack_size: 5 } }
  const [customerTier, setCustomerTier] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth', { state: { from: { pathname: '/shop' } } });
      return;
    }
    fetchProducts();
    fetchCustomerTier();
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/shop/products`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
        // Set default selections
        const defaults = {};
        data.forEach(p => {
          defaults[p.product_id] = {};
          if (p.has_colors && p.colors?.length > 0) {
            const inStockColor = p.colors.find(c => c.in_stock && c.stock_quantity > 0);
            defaults[p.product_id].color = inStockColor ? inStockColor.name : p.colors[0].name;
          }
          if (p.has_pack_sizes && p.pack_sizes?.length > 0) {
            const inStockSize = p.pack_sizes.find(s => s.in_stock && s.stock_quantity > 0);
            defaults[p.product_id].pack_size = inStockSize ? inStockSize.quantity : p.pack_sizes[0].quantity;
          }
        });
        setSelectedOptions(defaults);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerTier = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      if (res.ok) {
        const userData = await res.json();
        if (userData.pricing_tier) {
          setCustomerTier(userData.pricing_tier);
        }
      }
    } catch (e) { console.error('Error fetching customer tier:', e); }
  };

  const getProductImage = (product) => {
    const opts = selectedOptions[product.product_id] || {};
    if (product.has_colors && opts.color && product.colors) {
      const selectedColor = product.colors.find(c => c.name === opts.color);
      if (selectedColor?.image_url) return `${API_URL}${selectedColor.image_url}`;
    }
    return product.image_url ? `${API_URL}${product.image_url}` : null;
  };

  const getProductPrice = (product) => {
    const opts = selectedOptions[product.product_id] || {};
    if (product.has_pack_sizes && opts.pack_size) {
      const ps = product.pack_sizes?.find(s => s.quantity === opts.pack_size);
      return ps ? ps.price : product.base_price;
    }
    return product.base_price;
  };

  const isSelectedInStock = (product) => {
    const opts = selectedOptions[product.product_id] || {};
    if (product.has_colors && opts.color) {
      const c = product.colors?.find(cl => cl.name === opts.color);
      if (c && (!c.in_stock || c.stock_quantity <= 0)) return false;
    }
    if (product.has_pack_sizes && opts.pack_size) {
      const ps = product.pack_sizes?.find(s => s.quantity === opts.pack_size);
      if (ps && (!ps.in_stock || ps.stock_quantity <= 0)) return false;
    }
    // Simple product — check product-level stock
    if (!product.has_colors && !product.has_pack_sizes) {
      if (product.in_stock === false || (product.stock_quantity !== undefined && product.stock_quantity <= 0)) return false;
    }
    return true;
  };

  const getSelectedStock = (product) => {
    const opts = selectedOptions[product.product_id] || {};
    if (product.has_colors && opts.color) {
      const c = product.colors?.find(cl => cl.name === opts.color);
      return c?.stock_quantity || 0;
    }
    if (product.has_pack_sizes && opts.pack_size) {
      const ps = product.pack_sizes?.find(s => s.quantity === opts.pack_size);
      return ps?.stock_quantity || 0;
    }
    // Simple product — use product-level stock
    return product.stock_quantity || 0;
  };

  const handleAddToCart = (product) => {
    const opts = selectedOptions[product.product_id] || {};
    const price = getProductPrice(product);
    const stock = getSelectedStock(product);
    const qty = opts.quantity || 1;
    const available = isSelectedInStock(product);
    // Out of stock but the admin opted this product into pre-orders → allow it
    // as a pre-order line (inventory is deducted at fulfillment, not now).
    const isPreorder = !available && !!product.allow_preorder;

    if (!available && !isPreorder) {
      toast.error('This option is currently out of stock');
      return;
    }

    // Enforce max per order
    if (product.max_per_order && product.max_per_order > 0 && qty > product.max_per_order) {
      toast.error(`Maximum ${product.max_per_order} per order for this item`);
      return;
    }

    if (!isPreorder && qty > stock) {
      toast.error(`Only ${stock} available in stock`);
      return;
    }

    const cartItem = {
      product_id: product.product_id,
      name: product.name,
      price: price,
      quantity: qty,
      vial_size: opts.color || (opts.pack_size ? `${opts.pack_size}-pack` : 'standard'),
      item_type: 'shop',
      image_url: product.image_url,
      stock_quantity: stock,
      discount_eligible: product.discount_eligible,
      is_preorder: isPreorder
    };

    if (opts.color) cartItem.color = opts.color;
    if (opts.pack_size) cartItem.pack_size = opts.pack_size;

    addItem(cartItem);
    if (isPreorder) {
      toast.success(`${product.name} added as a pre-order — we'll ship it as soon as stock arrives.`);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 mb-4">Members Only</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Shop</h1>
            <p className="mt-3 text-slate-400 text-base sm:text-lg">
              Pens, cartridges, and accessories for your research needs.
            </p>
          </div>
        </div>
      </div>

      {/* Discount notice for affiliates */}
      {customerTier && (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="discount-notice">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Your {customerTier.name} discount applies only to research peptide vials.
              </p>
              <p className="text-xs text-amber-600 mt-0.5">
                Some shop items may not be eligible for discounts. Items marked "No Discount" are excluded.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Products */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="w-full h-48 bg-slate-100 rounded-lg mb-4" />
                <div className="h-5 bg-slate-100 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-600">No products available yet</h2>
            <p className="text-slate-400 mt-2">Check back soon for new accessories and supplies.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => {
              const opts = selectedOptions[product.product_id] || {};
              const price = getProductPrice(product);
              const inStock = isSelectedInStock(product);

              return (
                <div key={product.product_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`shop-card-${product.product_id}`}>
                  {/* Product Image */}
                  <div className="relative aspect-square bg-slate-50 overflow-hidden">
                    {getProductImage(product) ? (
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 p-4"
                        key={opts.color || 'default'}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-20 w-20 text-slate-200" />
                      </div>
                    )}
                    {!inStock && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Badge className={`text-white text-sm px-3 py-1 ${product.allow_preorder ? 'bg-amber-500' : 'bg-red-600'}`}>
                          {product.allow_preorder ? 'Pre-Order' : 'Out of Stock'}
                        </Badge>
                      </div>
                    )}
                    {!product.discount_eligible && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-slate-900/80 text-white text-xs">No Discount</Badge>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{product.name}</h3>
                        <Badge className="mt-1 bg-slate-100 text-slate-600 text-xs">
                          {product.category === 'pens' ? 'Pen / Device' :
                           product.category === 'cartridges' ? 'Cartridges' :
                           product.category === 'supplies' ? 'Supplies' : 'Accessory'}
                        </Badge>
                      </div>
                      <p className="text-xl font-bold text-teal-700">${price.toFixed(2)}</p>
                    </div>

                    {product.description && (
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">{product.description}</p>
                    )}

                    {/* Stock availability */}
                    <div className="mt-3 flex items-center gap-2" data-testid={`stock-info-${product.product_id}`}>
                      {inStock ? (
                        <>
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-sm font-medium text-green-700">In Stock</span>
                          {getSelectedStock(product) > 0 && (
                            <span className="text-xs text-slate-400">({getSelectedStock(product)} available)</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                          <span className="text-sm font-medium text-red-600">Out of Stock</span>
                        </>
                      )}
                    </div>

                    {/* Color Selection */}
                    {product.has_colors && product.colors?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-slate-600 mb-2">Color: {opts.color}</p>
                        <div className="flex flex-wrap gap-2">
                          {product.colors.map(color => {
                            const isSelected = opts.color === color.name;
                            const available = color.in_stock && color.stock_quantity > 0;
                            return (
                              <button
                                key={color.name}
                                onClick={() => setSelectedOptions(prev => ({
                                  ...prev,
                                  [product.product_id]: { ...prev[product.product_id], color: color.name }
                                }))}
                                className={`relative w-9 h-9 rounded-full border-2 transition-all ${
                                  isSelected ? 'border-teal-500 ring-2 ring-teal-200' :
                                  available ? 'border-slate-300 hover:border-slate-400' : 'border-slate-200 opacity-40'
                                }`}
                                style={{ backgroundColor: color.hex }}
                                title={`${color.name}${available ? ` (${color.stock_quantity} in stock)` : ' (Out of stock)'}`}
                                data-testid={`color-option-${color.name.toLowerCase()}`}
                              >
                                {isSelected && (
                                  <Check className={`absolute inset-0 m-auto h-4 w-4 ${color.hex === '#000000' || color.hex === '#2563EB' || color.hex === '#9333EA' || color.hex === '#DC2626' ? 'text-white' : 'text-slate-900'}`} />
                                )}
                                {!available && (
                                  <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="w-full h-0.5 bg-red-500 rotate-45 absolute" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Pack Size Selection */}
                    {product.has_pack_sizes && product.pack_sizes?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-slate-600 mb-2">Pack Size</p>
                        <div className="flex flex-wrap gap-2">
                          {product.pack_sizes.map(ps => {
                            const isSelected = opts.pack_size === ps.quantity;
                            const available = ps.in_stock && ps.stock_quantity > 0;
                            return (
                              <button
                                key={ps.quantity}
                                onClick={() => setSelectedOptions(prev => ({
                                  ...prev,
                                  [product.product_id]: { ...prev[product.product_id], pack_size: ps.quantity }
                                }))}
                                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                  isSelected ? 'border-teal-500 bg-teal-50 text-teal-700' :
                                  available ? 'border-slate-200 hover:border-slate-300 text-slate-700' :
                                  'border-slate-100 text-slate-300 cursor-not-allowed'
                                }`}
                                disabled={!available}
                                data-testid={`size-option-${ps.quantity}`}
                              >
                                <span className="block">{ps.quantity}-pack</span>
                                <span className="block text-xs mt-0.5">${ps.price.toFixed(2)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Quantity selector for simple products */}
                    {!product.has_colors && !product.has_pack_sizes && inStock && (
                      <div className="mt-4" data-testid={`quantity-selector-${product.product_id}`}>
                        <p className="text-xs font-medium text-slate-600 mb-2">
                          Quantity
                          {product.max_per_order > 0 && (
                            <span className="text-amber-600 ml-1">(max {product.max_per_order} per order)</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedOptions(prev => ({
                              ...prev,
                              [product.product_id]: {
                                ...prev[product.product_id],
                                quantity: Math.max(1, (prev[product.product_id]?.quantity || 1) - 1)
                              }
                            }))}
                            className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
                            data-testid={`qty-minus-${product.product_id}`}
                          >
                            -
                          </button>
                          <span className="w-10 text-center font-medium text-slate-900" data-testid={`qty-display-${product.product_id}`}>
                            {opts.quantity || 1}
                          </span>
                          <button
                            onClick={() => {
                              const current = opts.quantity || 1;
                              const maxQty = product.max_per_order > 0
                                ? Math.min(product.max_per_order, product.stock_quantity || 999)
                                : product.stock_quantity || 999;
                              if (current < maxQty) {
                                setSelectedOptions(prev => ({
                                  ...prev,
                                  [product.product_id]: {
                                    ...prev[product.product_id],
                                    quantity: current + 1
                                  }
                                }));
                              }
                            }}
                            className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
                            data-testid={`qty-plus-${product.product_id}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Add to Cart */}
                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={!inStock && !product.allow_preorder}
                      className={`w-full mt-5 ${inStock ? 'bg-teal-700 hover:bg-teal-800 text-white' : (product.allow_preorder ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed')}`}
                      data-testid={`add-to-cart-${product.product_id}`}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      {inStock ? 'Add to Cart' : (product.allow_preorder ? 'Pre-Order' : 'Out of Stock')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
